/** Create/edit form for a Claim. Validated against `createClaimSchema`. */
import { useForm } from "@refinedev/react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import {
  CLAIM_TYPES,
  CLAIM_STATUS,
  createClaimSchema,
  type CreateClaimInput,
} from "@aos/shared";

import { FormShell } from "../skills/_components/FormShell";
import {
  NumberField,
  optionsFromEnum,
  SelectField,
  TextAreaField,
  TextField,
} from "../skills/_components/form";

export interface ClaimFormProps {
  action: "create" | "edit";
}

export function ClaimForm({ action }: ClaimFormProps) {
  const form = useForm<CreateClaimInput>({
    resolver: zodResolver(createClaimSchema),
    refineCoreProps: { resource: "claims", action },
  });

  const {
    refineCore: { onFinish, formLoading },
    handleSubmit,
  } = form;

  return (
    <FormShell
      title={action === "create" ? "New Claim" : "Edit Claim"}
      resource="claims"
      isSubmitting={formLoading}
      onSubmit={handleSubmit((values) => onFinish(values))}
    >
      <TextField
        form={form}
        name="title"
        label="Title"
        placeholder="Adopt evidence-based phase transitions"
      />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <SelectField
          form={form}
          name="claim_type"
          label="Claim Type"
          options={optionsFromEnum(CLAIM_TYPES)}
        />
        <SelectField
          form={form}
          name="status"
          label="Status"
          options={optionsFromEnum(CLAIM_STATUS)}
          allowEmpty
        />
      </div>
      <TextAreaField
        form={form}
        name="statement"
        label="Statement"
        placeholder="The full assertion this claim makes…"
      />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <TextField
          form={form}
          name="package_id"
          label="Package ID"
          placeholder="pkg_… (optional)"
        />
        <TextField
          form={form}
          name="artifact_id"
          label="Artifact ID"
          placeholder="art_… (optional)"
        />
      </div>
      <NumberField
        form={form}
        name="confidence_score"
        label="Confidence Score"
        placeholder="0.0 – 1.0"
      />
    </FormShell>
  );
}

export default ClaimForm;
